import type { PersistedTourDraft } from '@/entities/tour'
import type { TourFormState } from '@/features/tour/tour-form-model'
import type { ITour, ITourPoi, TourBasicInfoState } from '@/shared/api/types/tour-types'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { buildTourDraft, hasTourDraftContent } from '@/entities/tour'
import {
  useCreateTranslationDraft,
  useLocalizedTranslationJobController,
} from '@/features/localized-translation'
import {
  buildTourBasicInfoSnapshot,
  buildTourCoordinatesSnapshot,
  buildTourMediaSnapshot,
  buildTourPoisSnapshot,
  buildTourTranslationSnapshot,
  deriveBasicInfoFromTour,
  sortTourPoisByOrder,
} from '@/features/tour/tour-form-model'
import { normalizeTourCoordinates } from '@/features/tour/tour-route-map-utils'
import { ReasonType, TRANSLATION_JOB_SCOPE } from '@/shared/constants'
import { DEFAULT_TOUR_BASIC_INFO } from '@/shared/constants/tour-form'

interface UseTourFormStateParams {
  isEdit: boolean
  tourId: string | undefined
  tenantId: string
  /** The tour being edited; `undefined` while loading or in create mode. */
  tour: ITour | undefined
}

/** Snapshots of each section taken when the form loaded or was last saved; "dirty" means "differs from this". */
interface TourBaselines {
  translations: string
  basicInfo: string
  coordinates: string
  media: string
  pois: string
}

const INITIAL_FORM_STATE: TourFormState = {
  basicInfo: DEFAULT_TOUR_BASIC_INFO,
  translations: [],
  selectedPois: [],
  routeCoordinates: [],
}

function buildEmptyBaselines(): TourBaselines {
  return {
    translations: buildTourTranslationSnapshot([]),
    basicInfo: buildTourBasicInfoSnapshot(DEFAULT_TOUR_BASIC_INFO),
    coordinates: buildTourCoordinatesSnapshot([]),
    media: buildTourMediaSnapshot(),
    pois: '',
  }
}

/**
 * Owns the tour form values and everything derived from them: hydration (edit data or restored draft),
 * per-section dirty flags and the baselines the section "Save" buttons commit.
 */
export function useTourFormState({ isEdit, tourId, tenantId, tour }: UseTourFormStateParams) {
  const [formState, setFormState] = useState<TourFormState>(INITIAL_FORM_STATE)
  const [isActive, setIsActive] = useState(true)
  const [baselines, setBaselines] = useState<TourBaselines>(buildEmptyBaselines)
  /** POIs as stored on the server; the POI batch save diffs the current selection against these. */
  const serverPoisRef = useRef<ITourPoi[]>([])

  const { basicInfo, translations, selectedPois, routeCoordinates, thumbnail, banner } = formState

  // * VI: Ở chế độ tạo, bản dịch dùng id nháp theo tenant để job dịch và bản nháp gắn với đúng form đang mở. | EN: In create mode translations use a per-tenant draft id so translation jobs and the draft stay tied to the open form.
  const draftEntityId = useMemo(() => `draft:tour:${tenantId || 'unknown-tenant'}`, [tenantId])
  const translationEntityId = isEdit && tourId ? tourId : draftEntityId
  const { pendingSuccessJobs } = useLocalizedTranslationJobController({
    scope: TRANSLATION_JOB_SCOPE.TOUR,
    entityId: translationEntityId,
    audioEnabled: ReasonType.TRANSLATE_AUDIO_AVAILABLE,
  })

  const draftInput = useMemo(
    () => ({ basicInfo, translations, selectedPois, routeCoordinates, thumbnail, banner }),
    [banner, basicInfo, routeCoordinates, selectedPois, thumbnail, translations],
  )
  const tourDraft = useMemo(() => buildTourDraft(draftInput), [draftInput])
  const hasTourDraft = useMemo(
    () => hasTourDraftContent(draftInput, DEFAULT_TOUR_BASIC_INFO),
    [draftInput],
  )
  const hydrateTourDraft = useCallback((persistedDraft?: PersistedTourDraft) => {
    setFormState({
      basicInfo: persistedDraft?.basicInfo ?? DEFAULT_TOUR_BASIC_INFO,
      translations: persistedDraft?.translations ?? [],
      selectedPois: persistedDraft?.selectedPois ?? [],
      routeCoordinates: persistedDraft?.routeCoordinates ?? [],
      ...(persistedDraft?.thumbnail ? { thumbnail: persistedDraft.thumbnail } : {}),
      ...(persistedDraft?.banner ? { banner: persistedDraft.banner } : {}),
    })
    setIsActive(true)
    setBaselines(buildEmptyBaselines())
  }, [])
  const { clearCurrentJobs } = useCreateTranslationDraft({
    enabled: !isEdit,
    scope: TRANSLATION_JOB_SCOPE.TOUR,
    entityId: translationEntityId,
    audioEnabled: ReasonType.TRANSLATE_AUDIO_AVAILABLE,
    initSignature: `create:${tenantId}`,
    draft: tourDraft,
    hasContent: hasTourDraft,
    onHydrate: hydrateTourDraft,
  })

  // * VI: useLayoutEffect hydrate form ngay trước khi paint, tránh nháy khung trống khi dữ liệu tour đã có trong cache. | EN: useLayoutEffect hydrates the form before paint, avoiding an empty-frame flash when the tour is already cached.
  useLayoutEffect(() => {
    if (!isEdit || !tour) return
    const tourPois = sortTourPoisByOrder(tour.pois ?? [])
    const routeCoordinatesFromTour = normalizeTourCoordinates(tour.coordinates)
    const nextBasicInfo = deriveBasicInfoFromTour(tour, tourPois)

    // eslint-disable-next-line react-hooks/set-state-in-effect -- seed the editable form from the async-loaded tour (same pattern as the tenant detail page)
    setFormState((prev) => ({
      ...prev,
      basicInfo: nextBasicInfo,
      routeCoordinates: routeCoordinatesFromTour,
      translations: tour.localizedData,
      selectedPois: tourPois,
      ...(tour.thumbnail ? { thumbnail: tour.thumbnail } : {}),
      ...(tour.banner ? { banner: tour.banner } : {}),
    }))
    // * VI: Cập nhật baseline ngay sau hydrate để dirty-check đúng, tránh báo "đã chỉnh sửa" sai khi vừa mở form edit. | EN: Update baselines right after hydration so dirty checks are accurate and do not flag false edits on first load.
    setBaselines({
      translations: buildTourTranslationSnapshot(tour.localizedData),
      basicInfo: buildTourBasicInfoSnapshot(nextBasicInfo),
      coordinates: buildTourCoordinatesSnapshot(routeCoordinatesFromTour),
      media: buildTourMediaSnapshot(tour.banner, tour.thumbnail),
      pois: buildTourPoisSnapshot(tourPois),
    })
    serverPoisRef.current = tourPois
  }, [isEdit, tour])

  const translationSnapshot = useMemo(
    () => buildTourTranslationSnapshot(translations),
    [translations],
  )
  const basicInfoSnapshot = useMemo(() => buildTourBasicInfoSnapshot(basicInfo), [basicInfo])
  const coordinatesSnapshot = useMemo(
    () => buildTourCoordinatesSnapshot(routeCoordinates),
    [routeCoordinates],
  )
  const mediaSnapshot = useMemo(
    () => buildTourMediaSnapshot(banner, thumbnail),
    [banner, thumbnail],
  )

  // * VI: Edit: dirty = khác baseline đã lưu. Create: dirty = đã nhập bất cứ thứ gì khác giá trị mặc định (để cảnh báo khi rời trang). | EN: Edit: dirty = differs from the saved baseline. Create: dirty = anything beyond the defaults was entered (to warn on leave).
  const isTranslationDirty = isEdit
    ? translationSnapshot !== baselines.translations
    : translations.some((item) => item.name.trim() || item.description.trim())
  const isBasicInfoDirty = isEdit
    ? basicInfoSnapshot !== baselines.basicInfo
    : basicInfoSnapshot !== buildTourBasicInfoSnapshot(DEFAULT_TOUR_BASIC_INFO) ||
      selectedPois.length > 0
  const isCoordinatesDirty = isEdit
    ? coordinatesSnapshot !== baselines.coordinates
    : coordinatesSnapshot !== buildTourCoordinatesSnapshot([])
  const isMediaDirty = isEdit
    ? mediaSnapshot !== baselines.media
    : (banner ?? '').length > 0 || (thumbnail ?? '').length > 0
  const isStatusDirty = !isEdit && !isActive
  const hasUnsavedChanges =
    isTranslationDirty || isBasicInfoDirty || isCoordinatesDirty || isMediaDirty || isStatusDirty
  /** POI changes are batched behind their own button, so they are tracked apart from the form dirty flag. */
  const isPoisBatchDirty = isEdit ? buildTourPoisSnapshot(selectedPois) !== baselines.pois : false

  const setTranslations = useCallback(
    (next: TourFormState['translations']) =>
      setFormState((prev) => ({ ...prev, translations: next })),
    [],
  )
  const setSelectedPois = useCallback(
    (next: ITourPoi[]) => setFormState((prev) => ({ ...prev, selectedPois: next })),
    [],
  )
  /** Functional update, so several quick additions in the same tick never overwrite each other. */
  const updateSelectedPois = useCallback(
    (updater: (prev: ITourPoi[]) => ITourPoi[]) =>
      setFormState((prev) => ({ ...prev, selectedPois: updater(prev.selectedPois) })),
    [],
  )
  const setRouteCoordinates = useCallback(
    (next: TourFormState['routeCoordinates']) =>
      setFormState((prev) => ({ ...prev, routeCoordinates: next })),
    [],
  )
  const updateBasicInfo = useCallback(
    (updater: (prev: TourBasicInfoState) => TourBasicInfoState) =>
      setFormState((prev) => ({ ...prev, basicInfo: updater(prev.basicInfo) })),
    [],
  )
  const setBanner = useCallback(
    (url: string) => setFormState((prev) => ({ ...prev, banner: url })),
    [],
  )
  const setThumbnail = useCallback(
    (url: string) => setFormState((prev) => ({ ...prev, thumbnail: url })),
    [],
  )

  /** Called after a section saved successfully, so it stops counting as dirty. */
  const baselineCommitters = {
    translations: () => setBaselines((prev) => ({ ...prev, translations: translationSnapshot })),
    basicInfo: () => setBaselines((prev) => ({ ...prev, basicInfo: basicInfoSnapshot })),
    coordinates: (saved: TourFormState['routeCoordinates']) => {
      setBaselines((prev) => ({ ...prev, coordinates: buildTourCoordinatesSnapshot(saved) }))
      setRouteCoordinates(saved)
    },
    media: () => setBaselines((prev) => ({ ...prev, media: mediaSnapshot })),
    pois: (saved: ITourPoi[]) => {
      serverPoisRef.current = [...saved]
      setBaselines((prev) => ({ ...prev, pois: buildTourPoisSnapshot(saved) }))
    },
  }

  return {
    formState,
    setFormState,
    updateBasicInfo,
    setTranslations,
    setSelectedPois,
    updateSelectedPois,
    setRouteCoordinates,
    setBanner,
    setThumbnail,
    isActive,
    setIsActive,
    translationEntityId,
    pendingSuccessJobs,
    clearCurrentJobs,
    serverPoisRef,
    hasUnsavedChanges,
    isBasicInfoDirty,
    isCoordinatesDirty,
    isMediaDirty,
    isPoisBatchDirty,
    commitBaseline: baselineCommitters,
  }
}
